import type { MessageDescriptor } from 'react-intl';
import { lyricTimingMessages } from './lyricTimingMessages';

export interface LyricTimingRefusal {
  /** The HTTP status, or null for a network error. */
  statusCode: number | null;
  /** `cause.code` of the API (`M24_LYRIC_TIMING_ORDER`, `M24_NO_LYRICS`, ...), or null. */
  code: string | null;
  /** Only on `M24_LYRIC_TIMING_ORDER`: the line that breaks the order. */
  index: number | null;
}

/**
 * The refusal of a lyric timing call. `customBaseQuery` puts the REST `cause` on `m24Limit`; the
 * lyric timing routes send `{ code, reason, index? }` there (music24-api `lyricTimingError.ts`).
 */
export function lyricTimingRefusal(error: unknown): LyricTimingRefusal {
  const { statusCode, m24Limit } = (error ?? {}) as {
    statusCode?: unknown;
    m24Limit?: { code?: unknown; reason?: unknown; index?: unknown } | null;
  };
  const cause = m24Limit ?? {};
  let code: string | null = null;
  if (typeof cause.code === 'string') {
    code = cause.code;
  } else if (typeof cause.reason === 'string') {
    code = cause.reason;
  }
  return {
    statusCode: typeof statusCode === 'number' ? statusCode : null,
    code,
    index: typeof cause.index === 'number' && Number.isInteger(cause.index) ? cause.index : null,
  };
}

/** The sentence for a refused Re-sync (spec §5.9). A 429 names `LYRIC_RESYNC_PER_HOUR` (the caller gives it). */
export function resyncRefusalMessage(refusal: LyricTimingRefusal): MessageDescriptor {
  if (refusal.statusCode === 429) {
    return lyricTimingMessages.resyncLimit;
  }
  const byCode: Record<string, MessageDescriptor> = {
    M24_NO_LYRICS: lyricTimingMessages.resyncNoLyrics,
    M24_NO_MASTER: lyricTimingMessages.resyncNoMaster,
    M24_MASTER_PROCESSING: lyricTimingMessages.resyncMasterProcessing,
    M24_MASTER_FAILED: lyricTimingMessages.resyncMasterFailed,
  };
  return (refusal.code === null ? undefined : byCode[refusal.code]) ?? lyricTimingMessages.resyncError;
}
